import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import { Shield, Trash2, Edit, Users, HardDrive, LogOut, ChevronLeft, Settings2, Globe, Lock, Trash, Music } from 'lucide-react';

export default function AdminPanel() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [newTier, setNewTier] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [customStorage, setCustomStorage] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  
  // Site Settings State
  const [customUrl, setCustomUrl] = useState('');
  const [sslEnabled, setSslEnabled] = useState(true);
  
  // Security Settings State
  const [rateLimitingEnabled, setRateLimitingEnabled] = useState(true);
  const [maxLoginAttempts, setMaxLoginAttempts] = useState(5);
  const [lockoutDuration, setLockoutDuration] = useState(15);
  const [ddosProtectionEnabled, setDdosProtectionEnabled] = useState(true);
  const [usernameFilterEnabled, setUsernameFilterEnabled] = useState(true);
  const [bannedWords, setBannedWords] = useState('');
  
  // Database Tools State
  const [selectedUserForCleanup, setSelectedUserForCleanup] = useState(null);
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);
  const [cleanupAction, setCleanupAction] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchSecuritySettings();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/login');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This will delete all their data.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setNewTier(user.account_tier);
    setNewPassword('');
    setCustomStorage('');
    setShowEditDialog(true);
  };

  const handleUpdateUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const updateData = {};

      if (newTier !== editingUser.account_tier) {
        updateData.account_tier = newTier;
      }

      if (newPassword) {
        updateData.password = newPassword;
      }

      if (customStorage) {
        updateData.storage_limit = parseInt(customStorage) * 1073741824; // Convert GB to bytes
      }

      const response = await axios.put(`${API}/admin/users/${editingUser.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('User updated successfully');
      setShowEditDialog(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const formatStorage = (bytes) => {
    if (bytes === -1) return 'Unlimited';
    const gb = bytes / 1073741824;
    return `${gb.toFixed(2)} GB`;
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleClearCache = async () => {
    if (!confirm('Clear all browser cache? This will refresh the page.')) {
      return;
    }

    try {
      // Clear browser cache
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      // Clear localStorage except auth
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      localStorage.clear();
      if (token) localStorage.setItem('token', token);
      if (user) localStorage.setItem('user', user);
      
      toast.success('Cache cleared successfully!');
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Error clearing cache:', error);
      toast.error('Failed to clear cache');
    }
  };

  const handleSaveCustomUrl = () => {
    if (!customUrl) {
      toast.error('Please enter a custom URL');
      return;
    }
    
    // Save to localStorage for demo (in production, this would be saved to backend)
    localStorage.setItem('customUrl', customUrl);
    toast.success('Custom URL saved successfully!');
  };

  const handleToggleSSL = (enabled) => {
    setSslEnabled(enabled);
    localStorage.setItem('sslEnabled', enabled ? 'true' : 'false');
    toast.success(`SSL ${enabled ? 'enabled' : 'disabled'} successfully!`);
  };

  const fetchSecuritySettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/security/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const settings = response.data;
      setRateLimitingEnabled(settings.rate_limiting_enabled);
      setMaxLoginAttempts(settings.max_login_attempts);
      setLockoutDuration(settings.lockout_duration_minutes);
      setDdosProtectionEnabled(settings.ddos_protection_enabled);
      setUsernameFilterEnabled(settings.username_filter_enabled);
      setBannedWords((settings.banned_words || []).join(', '));
    } catch (error) {
      console.error('Error fetching security settings:', error);
    }
  };

  const handleSaveSecuritySettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const settings = {
        rate_limiting_enabled: rateLimitingEnabled,
        max_login_attempts: parseInt(maxLoginAttempts),
        lockout_duration_minutes: parseInt(lockoutDuration),
        ddos_protection_enabled: ddosProtectionEnabled,
        username_filter_enabled: usernameFilterEnabled,
        banned_words: bannedWords.split(',').map(w => w.trim()).filter(w => w)
      };

      await axios.put(`${API}/admin/security/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Security settings saved successfully!');
    } catch (error) {
      console.error('Error saving security settings:', error);
      toast.error('Failed to save security settings');
    }
  };

  // Database Cleanup Functions
  const handleCleanUserDatabase = async (user) => {
    setSelectedUserForCleanup(user);
    setCleanupAction('database');
    setShowCleanupDialog(true);
  };

  const handleDeleteUserFiles = async (user) => {
    setSelectedUserForCleanup(user);
    setCleanupAction('files');
    setShowCleanupDialog(true);
  };

  const handleFixUserDatabase = async (user) => {
    setSelectedUserForCleanup(user);
    setCleanupAction('fix');
    setShowCleanupDialog(true);
  };

  const executeCleanup = async () => {
    if (!selectedUserForCleanup) return;

    try {
      const token = localStorage.getItem('token');
      let response;

      switch (cleanupAction) {
        case 'database':
          response = await axios.post(`${API}/admin/users/${selectedUserForCleanup.id}/clean-database`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success(`Database cleaned: ${response.data.deleted.tracks} tracks, ${response.data.deleted.crates} crates, ${response.data.deleted.favorites} favorites deleted`);
          break;

        case 'files':
          response = await axios.post(`${API}/admin/users/${selectedUserForCleanup.id}/delete-files`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success(`Files deleted: ${response.data.deleted_files} files removed`);
          break;

        case 'fix':
          response = await axios.post(`${API}/admin/users/${selectedUserForCleanup.id}/fix-database`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success(`Database fixed: ${response.data.fixed_tracks} tracks fixed, ${response.data.removed_tracks} invalid tracks removed`);
          break;
      }

      setShowCleanupDialog(false);
      fetchUsers(); // Refresh user list
    } catch (error) {
      console.error('Error during cleanup:', error);
      toast.error(error.response?.data?.detail || 'Cleanup operation failed');
    }
  };

  const handleCleanupOrphanedFiles = async () => {
    if (!confirm('This will delete ALL orphaned files across the system. Continue?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/cleanup/orphaned-files`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Orphaned files cleaned: ${response.data.deleted_files} files deleted`);
    } catch (error) {
      console.error('Error cleaning orphaned files:', error);
      toast.error('Failed to clean orphaned files');
    }
  };

  // Load settings on mount
  useEffect(() => {
    const savedUrl = localStorage.getItem('customUrl');
    const savedSSL = localStorage.getItem('sslEnabled');
    
    if (savedUrl) setCustomUrl(savedUrl);
    if (savedSSL !== null) setSslEnabled(savedSSL === 'true');
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Shield className="w-10 h-10 text-cyan-400" />
            <div>
              <h1 className="text-3xl font-bold text-white">Admin Panel</h1>
              <p className="text-gray-400">Manage users and system settings</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/admin/metadata')} className="bg-purple-600 hover:bg-purple-700">
              <Music className="w-4 h-4 mr-2" />
              Metadata Editor
            </Button>
            <Button onClick={() => navigate('/dashboard')} variant="outline" className="bg-gray-700 hover:bg-gray-600 border-gray-600">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <Button onClick={handleLogout} variant="outline" className="bg-red-900/50 hover:bg-red-800 border-red-700">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Tabs for different sections */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-gray-800 mb-6">
            <TabsTrigger value="users" className="data-[state=active]:bg-cyan-600">
              <Users className="w-4 h-4 mr-2" />
              User Management
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-purple-600">
              <Settings2 className="w-4 h-4 mr-2" />
              Site Settings
            </TabsTrigger>
            <TabsTrigger value="database" className="data-[state=active]:bg-orange-600">
              <HardDrive className="w-4 h-4 mr-2" />
              Database Tools
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-cyan-400" />
                    Total Users
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-white">{users.length}</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">DJ Starter</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-green-400">
                    {users.filter(u => u.account_tier === 'DJ Starter').length}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">DJ Pro</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-purple-400">
                    {users.filter(u => u.account_tier === 'DJ Pro').length}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Users Table */}
            <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">User Management</CardTitle>
            <CardDescription className="text-gray-400">View and manage all user accounts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left p-3 text-gray-400 font-semibold">User</th>
                    <th className="text-left p-3 text-gray-400 font-semibold">Tier</th>
                    <th className="text-left p-3 text-gray-400 font-semibold">Storage</th>
                    <th className="text-left p-3 text-gray-400 font-semibold">Created</th>
                    <th className="text-right p-3 text-gray-400 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id} className="border-b border-gray-700/50 hover:bg-gray-700/50">
                      <td className="p-3">
                        <div className="flex flex-col">
                          <span className="text-white font-medium">{user.username}</span>
                          <span className="text-sm text-gray-400">{user.email}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge className={
                          user.account_tier === 'Admin' ? 'bg-red-600' :
                          user.account_tier === 'DJ Pro' ? 'bg-purple-600' :
                          'bg-green-600'
                        }>
                          {user.account_tier}
                          {user.is_owner && ' 👑'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-col">
                          <span className="text-white text-sm">{formatStorage(user.storage_used)}</span>
                          <span className="text-xs text-gray-400">/ {formatStorage(user.storage_limit)}</span>
                        </div>
                      </td>
                      <td className="p-3 text-gray-400 text-sm">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            onClick={() => handleEditUser(user)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {!user.is_owner && (
                            <Button
                              size="sm"
                              onClick={() => handleDeleteUser(user.id, user.username)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          {/* Site Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            {/* Clear Cache Card */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Trash className="w-5 h-5 text-red-400" />
                  Cache Management
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Clear browser cache and temporary data
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                  <p className="text-sm text-gray-300 mb-4">
                    Clearing cache will remove all temporary files and may improve performance. This action requires a page reload.
                  </p>
                  <Button
                    onClick={handleClearCache}
                    className="bg-red-600 hover:bg-red-700 w-full"
                  >
                    <Trash className="w-4 h-4 mr-2" />
                    Clear All Cache
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Custom URL Card */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-cyan-400" />
                  Custom Domain
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Set a custom URL for your application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Custom URL</Label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="https://yourdomain.com"
                      value={customUrl}
                      onChange={(e) => setCustomUrl(e.target.value)}
                      className="bg-gray-900 border-gray-600 text-white flex-1"
                    />
                    <Button
                      onClick={handleSaveCustomUrl}
                      className="bg-cyan-600 hover:bg-cyan-700"
                    >
                      Save
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400">
                    Enter your custom domain URL (e.g., https://dj.yourdomain.com)
                  </p>
                </div>
                {customUrl && (
                  <div className="bg-green-900/20 border border-green-500/30 rounded p-3">
                    <p className="text-sm text-green-300">
                      ✓ Custom URL configured: <strong>{customUrl}</strong>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* SSL Security Card */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Lock className="w-5 h-5 text-green-400" />
                  SSL Security
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Enable or disable SSL/HTTPS encryption
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between bg-gray-900 p-4 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${sslEnabled ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
                    <div>
                      <Label className="text-white">SSL/HTTPS Encryption</Label>
                      <p className="text-xs text-gray-400 mt-1">
                        {sslEnabled ? 'Secure connection enabled' : 'Connection not secure'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={sslEnabled}
                    onCheckedChange={handleToggleSSL}
                  />
                </div>
                {sslEnabled && (
                  <div className="bg-green-900/20 border border-green-500/30 rounded p-3 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    <p className="text-sm text-green-300">
                      ✓ SSL encryption is <strong>active</strong>. Your connection is secure.
                    </p>
                  </div>
                )}
                {!sslEnabled && (
                  <div className="bg-red-900/20 border border-red-500/30 rounded p-3">
                    <p className="text-sm text-red-300">
                      ⚠ Warning: SSL is disabled. Your data may be vulnerable.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Security Protection Card */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-400" />
                  Security & Protection
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Configure login limits, DDoS protection, and username filtering
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Rate Limiting */}
                <div className="space-y-4 p-4 bg-gray-900 rounded-lg border border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${rateLimitingEnabled ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
                      <div>
                        <Label className="text-white">Login Rate Limiting</Label>
                        <p className="text-xs text-gray-400 mt-1">
                          Prevent brute force attacks by limiting login attempts
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={rateLimitingEnabled}
                      onCheckedChange={setRateLimitingEnabled}
                    />
                  </div>

                  {rateLimitingEnabled && (
                    <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-700">
                      <div className="space-y-2">
                        <Label className="text-white text-sm">Max Login Attempts</Label>
                        <Input
                          type="number"
                          min="3"
                          max="10"
                          value={maxLoginAttempts}
                          onChange={(e) => setMaxLoginAttempts(e.target.value)}
                          className="bg-gray-800 border-gray-600 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white text-sm">Lockout Duration (minutes)</Label>
                        <Input
                          type="number"
                          min="5"
                          max="60"
                          value={lockoutDuration}
                          onChange={(e) => setLockoutDuration(e.target.value)}
                          className="bg-gray-800 border-gray-600 text-white"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* DDoS Protection */}
                <div className="space-y-4 p-4 bg-gray-900 rounded-lg border border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${ddosProtectionEnabled ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
                      <div>
                        <Label className="text-white">DDoS Protection</Label>
                        <p className="text-xs text-gray-400 mt-1">
                          Block malicious traffic and prevent service overload
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={ddosProtectionEnabled}
                      onCheckedChange={setDdosProtectionEnabled}
                    />
                  </div>
                  
                  {ddosProtectionEnabled && (
                    <div className="bg-green-900/20 border border-green-500/30 rounded p-3 flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      <p className="text-xs text-green-300">
                        ✓ DDoS protection active. Automatic IP blocking enabled.
                      </p>
                    </div>
                  )}
                </div>

                {/* Username Filter */}
                <div className="space-y-4 p-4 bg-gray-900 rounded-lg border border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${usernameFilterEnabled ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
                      <div>
                        <Label className="text-white">Username Content Filter</Label>
                        <p className="text-xs text-gray-400 mt-1">
                          Block inappropriate usernames during signup
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={usernameFilterEnabled}
                      onCheckedChange={setUsernameFilterEnabled}
                    />
                  </div>

                  {usernameFilterEnabled && (
                    <div className="space-y-2 pt-3 border-t border-gray-700">
                      <Label className="text-white text-sm">Banned Words (comma-separated)</Label>
                      <Input
                        type="text"
                        placeholder="admin, root, profanity, etc."
                        value={bannedWords}
                        onChange={(e) => setBannedWords(e.target.value)}
                        className="bg-gray-800 border-gray-600 text-white"
                      />
                      <p className="text-xs text-gray-400">
                        Default: admin, root, system, administrator, moderator, and profanity
                      </p>
                    </div>
                  )}
                </div>

                {/* Save Button */}
                <Button
                  onClick={handleSaveSecuritySettings}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Save Security Settings
                </Button>

                {/* Security Status Summary */}
                <div className="bg-cyan-900/20 border border-cyan-500/30 rounded p-4">
                  <h4 className="text-sm font-semibold text-cyan-300 mb-2">🛡️ Protection Status</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${rateLimitingEnabled ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-gray-300">Rate Limiting: {rateLimitingEnabled ? 'ON' : 'OFF'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${ddosProtectionEnabled ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-gray-300">DDoS Protection: {ddosProtectionEnabled ? 'ON' : 'OFF'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${usernameFilterEnabled ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-gray-300">Username Filter: {usernameFilterEnabled ? 'ON' : 'OFF'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${sslEnabled ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-gray-300">SSL/HTTPS: {sslEnabled ? 'ON' : 'OFF'}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Database Tools Tab */}
          <TabsContent value="database" className="space-y-6">
            {/* System Cleanup Card */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <HardDrive className="w-5 h-5 text-orange-400" />
                  System Database Cleanup
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Clean orphaned files and fix database issues
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                  <h4 className="text-white font-semibold mb-2">Clean Orphaned Files</h4>
                  <p className="text-sm text-gray-300 mb-4">
                    Remove audio files from server that don't have database entries. This frees up storage space.
                  </p>
                  <Button
                    onClick={handleCleanupOrphanedFiles}
                    className="bg-orange-600 hover:bg-orange-700 w-full"
                  >
                    <Trash className="w-4 h-4 mr-2" />
                    Clean Orphaned Files
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* User Database Management Card */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-cyan-400" />
                  User Database Management
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Clean, fix, or delete user data and files
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left p-3 text-gray-400 font-semibold">User</th>
                        <th className="text-left p-3 text-gray-400 font-semibold">Storage</th>
                        <th className="text-right p-3 text-gray-400 font-semibold">Database Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(user => (
                        <tr key={user.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                          <td className="p-3">
                            <div className="flex flex-col">
                              <span className="text-white font-medium">{user.username}</span>
                              <span className="text-sm text-gray-400">{user.email}</span>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex flex-col">
                              <span className="text-white text-sm">{formatStorage(user.storage_used)}</span>
                              <span className="text-xs text-gray-400">/ {formatStorage(user.storage_limit)}</span>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2 justify-end flex-wrap">
                              <Button
                                size="sm"
                                onClick={() => handleCleanUserDatabase(user)}
                                className="bg-red-600 hover:bg-red-700 text-xs"
                                disabled={user.is_owner}
                              >
                                <Trash className="w-3 h-3 mr-1" />
                                Clean DB
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleDeleteUserFiles(user)}
                                className="bg-orange-600 hover:bg-orange-700 text-xs"
                                disabled={user.is_owner}
                              >
                                <HardDrive className="w-3 h-3 mr-1" />
                                Delete Files
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleFixUserDatabase(user)}
                                className="bg-blue-600 hover:bg-blue-700 text-xs"
                              >
                                <Settings2 className="w-3 h-3 mr-1" />
                                Fix DB
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 p-4 bg-cyan-900/20 border border-cyan-500/30 rounded">
                  <h4 className="text-sm font-semibold text-cyan-300 mb-2">🛠️ Action Explanations</h4>
                  <ul className="text-xs text-gray-300 space-y-1">
                    <li><strong>Clean DB:</strong> Removes all tracks, crates, and favorites from database. Resets storage to 0.</li>
                    <li><strong>Delete Files:</strong> Deletes all uploaded audio files from server storage.</li>
                    <li><strong>Fix DB:</strong> Removes invalid entries, fixes missing metadata, recalculates storage usage.</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Cleanup Confirmation Dialog */}
      <Dialog open={showCleanupDialog} onOpenChange={setShowCleanupDialog}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400">Confirm Database Action</DialogTitle>
            <DialogDescription className="text-gray-400">
              This action cannot be undone. Please confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {cleanupAction === 'database' && (
              <div className="bg-red-900/20 border border-red-500/30 rounded p-4">
                <p className="text-white mb-2">
                  <strong>Clean Database for: {selectedUserForCleanup?.username}</strong>
                </p>
                <p className="text-sm text-gray-300">
                  This will delete:
                </p>
                <ul className="text-sm text-gray-300 list-disc ml-6 mt-2">
                  <li>All tracks from database</li>
                  <li>All crates</li>
                  <li>All favorites</li>
                  <li>Reset storage to 0</li>
                </ul>
                <p className="text-xs text-red-300 mt-2">
                  Note: Physical files will remain. Use "Delete Files" to remove them.
                </p>
              </div>
            )}

            {cleanupAction === 'files' && (
              <div className="bg-orange-900/20 border border-orange-500/30 rounded p-4">
                <p className="text-white mb-2">
                  <strong>Delete Files for: {selectedUserForCleanup?.username}</strong>
                </p>
                <p className="text-sm text-gray-300">
                  This will permanently delete all uploaded audio files from the server.
                </p>
                <p className="text-xs text-orange-300 mt-2">
                  Database entries will remain. Use "Clean DB" to remove them.
                </p>
              </div>
            )}

            {cleanupAction === 'fix' && (
              <div className="bg-blue-900/20 border border-blue-500/30 rounded p-4">
                <p className="text-white mb-2">
                  <strong>Fix Database for: {selectedUserForCleanup?.username}</strong>
                </p>
                <p className="text-sm text-gray-300">
                  This will:
                </p>
                <ul className="text-sm text-gray-300 list-disc ml-6 mt-2">
                  <li>Remove tracks with missing files</li>
                  <li>Fix missing metadata</li>
                  <li>Clean orphaned crate references</li>
                  <li>Recalculate storage usage</li>
                </ul>
                <p className="text-xs text-blue-300 mt-2">
                  Safe operation - only removes invalid entries.
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button 
                onClick={executeCleanup} 
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Confirm Action
              </Button>
              <Button 
                onClick={() => setShowCleanupDialog(false)} 
                variant="outline" 
                className="flex-1 bg-gray-700"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Edit User: {editingUser?.username}</DialogTitle>
            <DialogDescription className="text-gray-400">
              Update user account settings
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Account Tier</Label>
              <Select value={newTier} onValueChange={setNewTier}>
                <SelectTrigger className="bg-gray-900 border-gray-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-600">
                  <SelectItem value="DJ Starter">DJ Starter (5GB)</SelectItem>
                  <SelectItem value="DJ Pro">DJ Pro (50GB)</SelectItem>
                  <SelectItem value="Admin">Admin (Unlimited)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Custom Storage Limit (GB)</Label>
              <Input
                type="number"
                placeholder="Leave empty to use tier default"
                value={customStorage}
                onChange={(e) => setCustomStorage(e.target.value)}
                className="bg-gray-900 border-gray-600"
              />
              <p className="text-xs text-gray-400">Override tier storage limit (optional)</p>
            </div>

            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                placeholder="Leave empty to keep current"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-gray-900 border-gray-600"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleUpdateUser} className="flex-1 bg-green-600 hover:bg-green-700">
                Save Changes
              </Button>
              <Button onClick={() => setShowEditDialog(false)} variant="outline" className="flex-1 bg-gray-700">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
